import { Component, Input, OnChanges, Output, EventEmitter, SimpleChanges, AfterViewInit, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { AuthService, BreadcrumbService, CompanyViewService, ProductService, ProductTypeWithColor, StateService, PaginatedProducts, RangeService, Utils } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Company, EnvironmentName, Product2, ProductName, ProductWorkflow, ProductWorkflowState, Range, WorkflowStateType } from 'situview-webapi-client';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { CreateGenericArtifactRequest, CreateGenericArtifactResponse, HomeStatus, SituRenderServiceWebApiClient } from 'siturenderservice-webapi-client';
import { ConfigService } from 'src/app/services/config.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { Observable, Subject, fromEvent, interval, of} from 'rxjs';
import { concatMap, debounceTime, map, takeWhile } from 'rxjs/operators';
import { Role } from 'src/app/shared/models/role.model';
import { MatDialog } from '@angular/material/dialog';
import { ProductDownloadDialogComponent } from '../product-download-dialog/product-download-dialog.component';
import { ProductDownloadRequest } from '../thumb-product-card/thumb-product-card.component';

@Component({
  selector: 'product-catalog-list',
  templateUrl: './product-catalog-list.component.html',
  styleUrls: ['./product-catalog-list.component.scss']
})

export class ProductCatalogListComponent extends SituBaseComponent implements OnChanges, AfterViewInit {
  @Input() companyIdFilter: string | undefined;
  @Input() productTypeFilter: string | undefined;
  @Input() productStateFilter: ProductWorkflowState | undefined = ProductWorkflowState.Live;
  @Input() templateNameFilter: string | undefined;
  @Input() tagGroupsFilter: string[] | undefined;
  @Input() rangeIdFilter: string | undefined;
  //@Input() autoRangeFilter: boolean = false;
  @Input() includeRanges: boolean = false;
  @Input() relatedTagsOrder?: string[];
  @Input() searchTerm: string = "";
  @Input() compact: boolean = false;
  @Input() isCatalog: boolean = false;
  @Input() multiSelect: boolean = false;
  @Output() onProductSelected: EventEmitter<Product2[]> = new EventEmitter();
  @Output() onFilterCompanyId: EventEmitter<string> = new EventEmitter();
  @Output() onFilterProductTypeId: EventEmitter<string> = new EventEmitter();
  @Output() onFilterProductState: EventEmitter<ProductWorkflowState> = new EventEmitter();
  @Output() onFilterProductRangeId: EventEmitter<string> = new EventEmitter();
  @Output() onSearchTermChanged: EventEmitter<string> = new EventEmitter();

  /* Search */
  pageSize = this.compact ? 8 : 20;
  pageNumber = 1;
  totalCount = 0;

  fullCatalogAccess: boolean = false;
  productStateAccess: boolean = false;
  
  productTypeNameFilter: string | undefined;
  companyNameFilter: string | undefined;
  productStateNameFilter: string | undefined;
  productRangeNameFilter: string | undefined;

  enabledProductsFilter = true;

  workflowStateType = WorkflowStateType;

  /* Data */
  dataSource = new Array<Product2>();
  selectedProducts = new Array<Product2>();

  readonly SituViewUrl: string;

  /* Loading */
  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private router: Router,
    private breadcrumbService: BreadcrumbService,
    private snackBar: MatSnackBar,
    private productService: ProductService,
    private companyViewService: CompanyViewService,
    private renderServiceClient: SituRenderServiceWebApiClient,
    private configService: ConfigService,
    private changeDetectorRef: ChangeDetectorRef,
    private clipboard: Clipboard,
    private dialog: MatDialog,
    private rangeService: RangeService) {
    super(authService, stateService);
    this.SituViewUrl = configService.config.situViewWebApiBaseUrl;

    this._subscriptions.push(this.searchTermChangedSubject.pipe( debounceTime(600) )
    .subscribe(() => {
      this.searchTermChanged();
    }))
  }

  protected onSelectedCompanyChange(): void {
    this.loadProducts(true);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.fullCatalogAccess = this.authService.hasRole(Role.ProductCatalog) || this.authService.hasSituAccess();
    this.productStateAccess = this.fullCatalogAccess || this.authService.hasRole(Role.ExternalProductContractor) || this.authService.hasRole(Role.CompanyProductPublisher);

    this.filteredCompanies = this.companyControl.valueChanges.pipe(
      map((value) => {
        return this.filterCompanies(value);
      })
    );

    this.filteredProductTypes = this.productTypeControl.valueChanges.pipe(
      map((value) => {
        return this.filterProductTypes(value);
      })
    );

    this.filteredProductStates = this.productStateControl.valueChanges.pipe(
      map((value) => {
        return this.filterProductStates(value);
      })
    );

    this.filteredProductRanges = this.productRangeControl.valueChanges.pipe(
      map((value) => {
        if (typeof value === 'string') {
          return this.filterProductRanges(value);
        } else {
          return this.filterProductRanges();
        }
      })
    );

    this.loadCompanies();
    this.loadProductRanges();

    if (this.productStateAccess) {
      this.loadProductStates();
    }
    
    this.loadProductTypes();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.observer && this.showMoreProductsMarker?.nativeElement) {
      this.observer.unobserve(this.showMoreProductsMarker.nativeElement);
    }
    if (this.searchTermChangedSubject) {
      this.searchTermChangedSubject.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    this.setupIntersectionObserver();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.companyIdFilter &&
      changes.companyIdFilter.currentValue !== changes.companyIdFilter.previousValue &&
      changes.companyIdFilter.currentValue !== this.companyIdFilter) {
      if (changes.companyIdFilter.currentValue) {
        this.selectCompanyByProduct(changes.companyIdFilter.currentValue);
      } else {
        this.clearCompany();
      }
    }

    if (changes.productTypeFilter &&
      changes.productTypeFilter.currentValue !== changes.productTypeFilter.previousValue &&
      changes.productTypeFilter.currentValue !== this.productTypeFilter) {
      if (changes.productTypeFilter.currentValue) {
        this.selectProductTypeById(changes.productTypeFilter.currentValue);
      } else {
        this.clearProductType();
      }
    }

    if (changes.productStateFilter &&
      changes.productStateFilter.currentValue !== changes.productStateFilter.previousValue &&
      changes.productStateFilter.currentValue !== this.productStateFilter) {
      if (changes.productStateFilter.currentValue) {
        this.selectProductState(changes.productStateFilter.currentValue);
      } else {
        this.clearProductState();
      }
    } else if (changes.productStateFilter && changes.productStateFilter.firstChange && !changes.productStateFilter.currentValue) {
      this.selectProductState(ProductWorkflowState.Live);
    }

    if (changes.searchTerm && !changes.searchTerm.firstChange &&
      changes.searchTerm.currentValue !== changes.searchTerm.previousValue &&
      changes.searchTerm.currentValue !== this.searchTerm
    ) {
      this.searchTerm = changes.searchTerm.currentValue;
      this.searchTermChanged();
    }

    if (changes.rangeIdFilter &&
      changes.rangeIdFilter.currentValue !== changes.previousValue &&
      changes.rangeIdFilter.currentValue !== this.rangeIdFilter) {
      if (changes.rangeIdFilter.currentValue) {
        this.selectProductRangeById(changes.rangeIdFilter.currentValue);
      } else {
        this.clearProductRange();
      }
    }

    if (changes.compact && changes.compact.currentValue !== changes.compact.previousValue) {
      this.pageSize = this.compact ? 8 : 20;
    }
  }

  @ViewChild('showMoreProductsMarker') showMoreProductsMarker: ElementRef | undefined;
  private observer: IntersectionObserver | undefined;
  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.hasMoreProductsToShow()) {
          this.showMoreProducts();
        }
      });
    });

    if (this.showMoreProductsMarker?.nativeElement) {
      this.observer.observe(this.showMoreProductsMarker.nativeElement);
    }
  }

  async showMoreProducts() {
    this.pageNumber++;
    await this.loadProducts(false);
  }

  async loadAllProducts() {
    while(this.hasMoreProductsToShow()) {
      await this.showMoreProducts();
    }
  }

  hasMoreProductsToShow(): boolean {
    return this.dataSource?.length < this.totalCount;
  }

  share(artifactId: string) {
    let link = this.configService.config.situLinkBaseUrl ?
          `https://product.${this.configService.config.situLinkBaseUrl}/${artifactId}` :
          `${this.configService.config.situPortalBaseUrl}/${artifactId}`;
    this.clipboard.copy(link);
    this.showMessage("Copied situLINK to clipboard!");
  }

  getProductTypeColor(productTypeId: string) {
    return this.allProductTypes.find(x => x.id === productTypeId)?.color;
  }

  getProductTypeName(productTypeId: string) {
    return this.allProductTypes.find(x => x.id === productTypeId)?.name;
  }

  getCompanyLogo(companyId: string) {
    return this.allCompanies.find(x => x.id === companyId)?.logoUrl;
  }

  getCompanyName(companyId: string) {
    return this.allCompanies.find(x => x.id === companyId)?.name;
  }

  getPreview(assetKey: string) {
    let request = new CreateGenericArtifactRequest();
    let companyPart = assetKey?.split("\\")[1];
    if (companyPart) {
      let company = this.allCompanies.find(x => x.name == companyPart);
      request.companyId = company ? company.id : this.selectedCompany?.id;
    } else {
      request.companyId = this.selectedCompany?.id;
    }
    request.callerReferenceKey = assetKey?.replace("assetinstance\\", "assetpreview\\");
    request.environment = EnvironmentName.Latest;
    request.environmentToUse = ProductName.Panorama;

    this.renderServiceClient.createGenericArtifact(request)
      .then((response: CreateGenericArtifactResponse) => {
        if (response.success) {
          if (response.status === HomeStatus.Complete) {
            this.showMessage("The Product Preview is complete!");
          } else {
            this.showMessage("We are generating a Product Preview!");
          }
        } else {
          this.showMessage("We could not generate the Product Preview!");
        }
      });
  }

  onProductDownloadRequest(request: ProductDownloadRequest) {
    this.dialog.open(ProductDownloadDialogComponent, {
      data: {
        url: `${this.configService.config.situViewWebApiBaseUrl}/home/getglb?artifactId=${request.artifactId}&filename=${request.file}`,
        fileName: request.fileName
      }
    });
  }

  onSelectProduct(product: Product2) {
    if (!this.multiSelect) {
      this.selectedProducts = [product];

      this.onProductSelected.emit(this.selectedProducts);
    } else {
        if (this.isSelectedProduct(product)) {
          this.selectedProducts = this.selectedProducts.filter(x => x.id !== product.id);
        } else {
          this.selectedProducts.push(product);
        }
    }
  }

  isSelectedProduct(product: Product2): boolean {
    // Don't show selection in catalog mode
    return !this.isCatalog && this.selectedProducts.some(x => x.id === product.id);
  }

  chooseSelectProducts() {
    var products = this.getSelectedProducts();
    this.onProductSelected.emit(products);
  }

  hasSelectedAllProducts(): boolean {
    return this.dataSource.every(x => this.isSelectedProduct(x));
  }

  selectAllProducts(select: boolean) {
    if (select) {
      this.dataSource.forEach(x => {
        if (!this.isSelectedProduct(x)) {
            this.selectedProducts.push(x);
          }
      });
    } else {
      this.selectedProducts = [];
    }
  }

  getSelectedProducts(): Product2[] {
    return this.selectedProducts;
  }

  getSelectedProductCount(): number {
    return this.getSelectedProducts().length;
  }

  private searchTermChangedSubject = new Subject();
  debounceSearchTermChanged() {
    this.searchTermChangedSubject.next();
  }

  searchTermChanged() {
    this.onSearchTermChanged.emit(this.searchTerm); 

    this.filterProducts();
  }

  onEnabledProductsFilter() {
    this.filterProducts();
  }

  filterProducts() {
    this.pageNumber = 1;
    this.loadProducts(true);
  }

  async loadProducts(clearExistingProducts: boolean) {
    if (this.loading) {
      return;
    }
    
    this.loading = true;

    var includeProductSpecifications = !this.isCatalog;

    try {
      var productPage = await this.productService.LoadPaginatedProducts(
        this.selectedCompany?.id!,
        undefined,
        this.companyIdFilter,
        this.productTypeFilter,
        this.templateNameFilter,
        this.productStateFilter,
        this.searchTerm,
        this.rangeIdFilter,
        this.relatedTagsOrder,
        includeProductSpecifications,
        false,
        this.includeRanges,
        false,
        this.enabledProductsFilter,
        this.tagGroupsFilter,
        this.pageNumber,
        this.pageSize
      );

      var products = productPage.products! as Product2[];
      if (clearExistingProducts) {
        this.dataSource = products;
      } else {
        this.dataSource.push(...products);
      }

      this.totalCount = productPage.totalItems!;

    } catch {
      this.showMessage("There was an issue loading products. Please refresh the page to try again.");
    } finally {
      this.loading = false;
    }
  }

  /*  Company AutoComplete Filter */
  companyControl = new FormControl();
  filteredCompanies: Observable<Company[]> | undefined;
  allCompanies: Company[] = [];

  private loadCompanies() {
    return this.companyViewService.LoadCompanies()
      .then((companies: Company[]) => {
        this.allCompanies = companies;
        
        this.companyNameFilter = this.allCompanies?.find(x => x.id === this.companyIdFilter)?.name;
        this.companyControl.setValue(this.companyNameFilter);
        this.companyControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });

        this.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
        this.productStateControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      });
  }

  onCompanySelected(event: any): void {
    this.selectCompanyByName(event.option.value);

    this.loadProductRanges();

    this.onFilterCompanyId.emit(this.companyIdFilter);
  }

  selectCompanyByName(companyName: string) {
    this.companyIdFilter = this.allCompanies?.find(x => x.name === companyName)?.id;
    this.companyNameFilter = companyName;
      
    this.filterProducts();
  }

  selectCompanyById(companyId: string) {
    this.companyIdFilter = companyId;
    this.companyNameFilter = this.allCompanies?.find(x => x.id === companyId)?.name;
      
    this.filterProducts();
  }

  selectCompanyByProduct(productCompanyId: string, filterProducts = true) {
    this.companyIdFilter = productCompanyId;
    this.companyNameFilter = this.allCompanies?.find(x => x.id === productCompanyId)?.name;

    this.companyControl.setValue(this.companyNameFilter);
    this.companyControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    this.onFilterCompanyId.emit(this.companyIdFilter);
      
    if (filterProducts) {
      this.filterProducts();
    }
  }

  onClearCompany(filterProducts = true) {
    this.clearCompany(filterProducts);
    this.onFilterCompanyId.emit(undefined);
  }

  clearCompany(filterProducts = true) {
    this.companyControl.reset();
    this.companyIdFilter = undefined;
    this.companyNameFilter = undefined;
    
    if (filterProducts) {
      this.filterProducts();
    }
  }

  private filterCompanies(searchTerm: string | undefined = undefined): Company[] {
    var companies = this.allCompanies;

    // Filter Company Names by the supplied Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      companies = companies.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    return companies;
  }
  /*  Company AutoComplete Filter */

  /*  ProductType AutoComplete Filter */
  productTypeControl = new FormControl();
  filteredProductTypes: Observable<ProductTypeWithColor[]> | undefined;
  allProductTypes: ProductTypeWithColor[] = [];
  
  private loadProductTypes() {
    return this.productService.LoadProductTypesWithColor()
      .then((productTypes: ProductTypeWithColor[]) => {
        this.allProductTypes = productTypes;
        
        this.productTypeNameFilter = this.allProductTypes?.find(x => x.id === this.productTypeFilter)?.name;
        this.productTypeControl.setValue(this.productTypeNameFilter);
        this.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      });
  }

  onProductTypeSelected(event: any): void {
    this.selectProductTypeByName(event.option.value);

    this.onFilterProductTypeId.emit(this.productTypeFilter);
  }

  selectProductTypeByName(productTypeName: string) {
    this.productTypeFilter = this.allProductTypes?.find(x => x.name === productTypeName)?.id;
    this.productTypeNameFilter = productTypeName;
      
    this.filterProducts();
  }

  selectProductTypeById(productTypeId: string) {
    this.productTypeFilter = productTypeId;
    this.productTypeNameFilter = this.allProductTypes?.find(x => x.id === productTypeId)?.name;
      
    this.filterProducts();
  }

  selectProductTypeByProduct(productTypeId: string) {
    this.productTypeFilter = productTypeId;
    this.productTypeNameFilter = this.allProductTypes?.find(x => x.id === productTypeId)?.name;

    this.productTypeControl.setValue(this.productTypeNameFilter);
    this.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    this.onFilterProductTypeId.emit(this.productTypeFilter);
      
    this.filterProducts();
  }

  onClearProductType() {
    this.clearProductType();

    this.onFilterProductTypeId.emit(undefined);
  }

  clearProductType() {
    this.productTypeControl.reset();
    this.productTypeFilter = undefined;
    this.productTypeNameFilter = undefined;
      
    this.filterProducts();
  }

  private filterProductTypes(searchTerm: string | undefined = undefined): ProductTypeWithColor[] {
    var productTypes = this.allProductTypes;

    // Filter by Template Name
    if (this.templateNameFilter) {
      var templateName = this.templateNameFilter.toLowerCase()
      productTypes = productTypes
        .filter(e => e.templateName?.toLowerCase() === templateName)
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productTypes = productTypes.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    return productTypes;
  }
  /*  ProductType AutoComplete Filter */

  /*  ProductState AutoComplete Filter */
  productStateControl = new FormControl();
  filteredProductStates: Observable<ProductWorkflow[]> | undefined;
  allProductStates: ProductWorkflow[] = [];
  
  private loadProductStates() {
    return this.productService.LoadProductWorkflowConfiguration()
      .then((productWorkflow: ProductWorkflow[]) => {
        this.allProductStates = productWorkflow;
        
        this.productStateNameFilter = this.allProductStates?.find(x => x.state === this.productStateFilter)?.name;
        this.productStateControl.setValue(this.productStateNameFilter);
        this.productStateControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      });
  }

  onProductStateSelected(event: any): void {
    this.selectProductStateByName(event.option.value);

    this.onFilterProductState.emit(this.productStateFilter);
  }

  selectProductState(productState: ProductWorkflowState) {
    this.productStateFilter = productState;
    this.productStateNameFilter = this.allProductStates?.find(x => x.state === productState)?.name;
      
    this.filterProducts();
  }

  selectProductStateByName(productStateName: string) {
    this.productStateFilter = this.allProductStates?.find(x => x.name === productStateName)?.state;
    this.productStateNameFilter = productStateName;
      
    this.filterProducts();
  }

  onClearProductState() {
    this.clearProductState();

    this.onFilterProductState.emit(undefined);
  }

  clearProductState() {
    this.productStateControl.reset();
    this.productStateFilter = undefined;
    this.productStateNameFilter = undefined;
      
    this.filterProducts();
  }

  private filterProductStates(searchTerm: string | undefined = undefined): ProductWorkflow[] {
    var productStates = this.allProductStates;

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productStates = productStates.filter(x => x.name).filter(state => state.name!.toLowerCase().includes(searchTerm!));
    }

    return productStates;
  }

  /*  ProductState AutoComplete Filter */

  /*  ProductRange AutoComplete Filter */
  productRangeControl = new FormControl();
  filteredProductRanges: Observable<Range[]> | undefined;
  allProductRanges: Range[] = [];
  private loadProductRanges() {
    return this.rangeService.ListSharedRanges(this.selectedCompany?.id!)
      .then((ranges: Range[]) => {
        this.allProductRanges = ranges;
        
        const range = this.allProductRanges?.find(x => x.id === this.rangeIdFilter);
        this.productRangeNameFilter = range?.name;
        this.productRangeControl.setValue(range);
        this.productRangeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      });
  }

  onProductRangeSelected(event: any): void {
    this.selectProductRangeByName(event.option.value);

    this.onFilterProductRangeId.emit(this.rangeIdFilter);
  }

  selectProductRangeByName(range: Range) {
    this.rangeIdFilter = range?.id;
    this.productRangeNameFilter = range?.name;
    
    this.setCompanyOnRangeSelection(range);
    this.filterProducts();
  }

  displayRange(range: Range): string {
    return range && range.name ? range.name : '';
  }

  selectProductRangeById(productRangeId: string) {
    this.rangeIdFilter = productRangeId;
    var range = this.allProductRanges?.find(x => x.id === productRangeId);
    this.productRangeNameFilter = range?.name;
      
    this.setCompanyOnRangeSelection(range);
    this.filterProducts();
  }

  selectProductRangeByProduct(productRangeId: string) {
    this.rangeIdFilter = productRangeId;
    var range = this.allProductRanges?.find(x => x.id === productRangeId);
    this.productRangeNameFilter = range?.name;

    this.productRangeControl.setValue(this.productRangeNameFilter);
    this.productRangeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    this.onFilterProductRangeId.emit(this.rangeIdFilter);
    
    this.setCompanyOnRangeSelection(range);
    this.filterProducts();
  }

  setCompanyOnRangeSelection(range?: Range){
    // Selecting a product range should deselect the filtered company if it is not the same as the ranges company
    if (this.companyIdFilter !== range?.companyId) {
      if (range?.companyId) {
        this.selectCompanyByProduct(range.companyId, false);
      } else {
        this.onClearCompany(false);
      }
    }
  }

  onClearProductRange() {
    this.clearProductRange();

    this.onFilterProductRangeId.emit(undefined);
  }

  clearProductRange() {
    this.productRangeControl.reset();
    this.rangeIdFilter = undefined;
    this.productRangeNameFilter = undefined;
      
    this.setCompanyOnRangeSelection(undefined);
    this.filterProducts();
  }

  private filterProductRanges(searchTerm: string | undefined = undefined): Range[] {
    var productRanges = this.allProductRanges;

    if (this.templateNameFilter) {
      let name = this.templateNameFilter.toLowerCase()
      productRanges = productRanges
        .filter(e => e.name?.toLowerCase() === name)
    }

    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productRanges = productRanges.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    if (this.companyIdFilter) {
      productRanges = productRanges = productRanges.sort((a, b) => {
        var aMatches = a.companyId === this.companyIdFilter;
        var bMatches = b.companyId === this.companyIdFilter;

        if (aMatches && !bMatches) return -1;
        if (!aMatches && bMatches) return 1;
        return 0; // maintain original order otherwise
      });
    }

    return productRanges;
  }
  /*  ProductRange AutoComplete Filter */

  pluralize(unit: string, value: number): string {
    return Utils.pluralize(unit, value);
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'Dismiss', {
      duration: 3000
    });
  }
}